import { isBefore } from 'date-fns'
import { sortBy } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { LearningItem } from '../../models/LearningItem'
import dueDateTimestamp from './dueDateTimestamp'

interface ScheduleItem {
  type: string
  date: Date
  endDate?: Date
  learningItem: LearningItem
  assignments: Exclude<LearningItem['userLearningAssignments'], null | undefined>
  info?: React.ReactNode[]
}

export default function scheduleItems({
  learningItems,
  t,
  currentUserUuid,
}: {
  learningItems: LearningItem[]
  t: ReturnType<typeof useTranslation>['t']
  currentUserUuid: string
}) {
  const items: ScheduleItem[] = []
  const now = new Date()
  for (const learningItem of learningItems) {
    // Don't show finished items
    if (learningItem.learningProgress?.completedAt) {
      continue
    }

    const userLearningAssignments = learningItem.userLearningAssignments || []

    const dueDateAssignments = sortBy(userLearningAssignments, ({ dueDate }) =>
      dueDate ? dueDate.toISOString() : 'never'
    )

    // Course
    if (learningItem.kind === 'onDemandCourse') {
      if (dueDateAssignments[0]?.dueDate) {
        items.push({
          type: 'due',
          date: dueDateAssignments[0].dueDate,
          learningItem,
          assignments: dueDateAssignments,
        })
      }
    }

    // Scheduled Course
    if (learningItem.kind === 'scheduledCourse') {
      const bookedEvent = learningItem.scheduledCourse.events.find(({ booking }) => booking)
      // User has booked an event, so show all the dates they have to attend.
      if (bookedEvent) {
        for (const date of bookedEvent.dates) {
          items.push({
            type: 'booked event',
            date: date.startDate,
            endDate: date.endDate,
            learningItem,
            assignments: dueDateAssignments,
            info: [
              date.meetingRoom,
              date.meetingAddress,
              date.meetingLink && (
                <a href={date.meetingLink} target="_blank">
                  {t('learning item.Meeting Link')}
                </a>
              ),
              dueDateAssignments[0]?.dueDate && t('learning item.due date is', { date: dueDateAssignments[0].dueDate }),
            ],
          })
        }
      }
      // Otherwise, if there is a due date, show the start dates of each event they could still book.
      else {
        const dueDate = dueDateAssignments[0]?.dueDate
        if (dueDate) {
          const assignmentItems = learningItem.scheduledCourse.events
            .filter(({ startDate }) => isBefore(now, startDate))
            .map((event) => ({
              type: 'bookable event',
              date: event.startDate,
              event,
              learningItem,
              assignments: dueDateAssignments,
              info: [dueDate && t('learning item.due date is', { date: dueDate }), t('learning item.bookable event')],
            }))

          // Remove events that end after the due date.
          const assignmentItemsWithinDueDate = assignmentItems.filter(({ event: { endDate } }) =>
            isBefore(endDate, dueDateTimestamp(dueDate))
          )

          // Indicate the last event before the due date.
          if (assignmentItemsWithinDueDate.length) {
            if (assignmentItemsWithinDueDate.length !== assignmentItems.length) {
              assignmentItemsWithinDueDate[assignmentItemsWithinDueDate.length - 1].info.push(
                t('learning item.last event before due date')
              )
            }

            items.push(...assignmentItemsWithinDueDate)
          }

          // In case there are no more events before the due date, show the due date instead.
          else {
            if (dueDate) {
              items.push({
                type: 'due',
                date: dueDate,
                learningItem,
                assignments: dueDateAssignments,
                info: [t('learning item.no events before due date')],
              })
            }
          }
        }

        // If there is no due date, find out if the current
        // user is a trainer related to the course.
        else {
          const datesForTrainer = getDatesWithCurrentUserAsTrainer(learningItem.scheduledCourse.events, currentUserUuid)

          for (const date of datesForTrainer) {
            items.push({
              type: 'trainer event',
              date: date.startDate,
              endDate: date.endDate,
              learningItem,
              assignments: dueDateAssignments,
              info: [
                date.meetingRoom,
                date.meetingAddress,
                date.meetingLink && (
                  <a href={date.meetingLink} target="_blank">
                    {t('learning item.Meeting Link')}
                  </a>
                ),
              ],
            })
          }
        }
      }
    }
  }

  return sortBy(items, 'date')
}

function getDatesWithCurrentUserAsTrainer(events: any, currentUserUuid: string) {
  const dates = []

  for (const event of events) {
    for (const date of event.dates) {
      for (const trainer of date.trainers) {
        if (trainer.uuid === currentUserUuid) {
          dates.push(date)
          break
        }
      }
    }
  }

  return dates
}
